import { __ } from "@wordpress/i18n";
import { useState } from "@wordpress/element";
import { X } from "lucide-react";

export function useConfirm() {
    const [isOpen, setIsOpen] = useState(false);
    const [onConfirm, setOnConfirm] = useState(null);
    const [message, setMessage] = useState("");
    const [btnText, setBtnText] = useState("");
    const [title, setTitle] = useState(
        __("Confirm Action", "wpbean-quantity-controls-for-woocommerce"),
    );
    const [type, setType] = useState("default"); // default, danger, warning

    const confirm = (message, options = {}) =>
        new Promise((resolve) => {
            setMessage(message);
            setTitle(
                options.title ||
                    __(
                        "Confirm Action",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
            );
            setBtnText(options.btnText || "");
            setType(options.type || "default");
            setIsOpen(true);
            setOnConfirm(() => (confirmed) => {
                resolve(confirmed);
                setIsOpen(false);
            });
        });

    const getIconConfig = () => {
        switch (type) {
            case "danger":
                return {
                    bg: "bg-red-100",
                    icon: (
                        <svg
                            className="w-6 h-6 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    ),
                    buttonBg: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
                    buttonText:
                        btnText ||
                        __(
                            "Delete",
                            "wpbean-quantity-controls-for-woocommerce",
                        ),
                };
            case "warning":
                return {
                    bg: "bg-yellow-100",
                    icon: (
                        <svg
                            className="w-6 h-6 text-yellow-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    ),
                    buttonBg:
                        "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
                    buttonText:
                        btnText ||
                        __(
                            "Continue",
                            "wpbean-quantity-controls-for-woocommerce",
                        ),
                };
            default:
                return {
                    bg: "bg-blue-100",
                    icon: (
                        <svg
                            className="w-6 h-6 text-blue-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    ),
                    buttonBg:
                        "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
                    buttonText:
                        btnText ||
                        __(
                            "Confirm",
                            "wpbean-quantity-controls-for-woocommerce",
                        ),
                };
        }
    };

    const iconConfig = getIconConfig();

    const ConfirmDialog = () =>
        isOpen ? (
            <div className="fixed inset-0 z-[99999] overflow-y-auto">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={() => onConfirm(false)}
                />

                {/* Dialog */}
                <div className="flex min-h-full items-center justify-center p-4">
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            type="button"
                            onClick={() => onConfirm(false)}
                            aria-label={__(
                                "Close dialog",
                                "wpbean-quantity-controls-for-woocommerce",
                            )}
                            className="absolute top-3 right-3 inline-flex items-center justify-center rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Content */}
                        <div className="p-6">
                            {/* Icon */}
                            <div
                                className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${iconConfig.bg} mb-4`}
                            >
                                {iconConfig.icon}
                            </div>
                            {/* Title */}
                            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                                {title}
                            </h3>
                            {/* Message */}
                            <p className="text-sm text-gray-600 text-center mb-6 max-h-48 overflow-y-auto whitespace-pre-wrap break-words">
                                {message}
                            </p>
                            {/* Actions */}
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                                <button
                                    onClick={() => onConfirm(false)}
                                    className="px-6 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all"
                                >
                                    {__(
                                        "Cancel",
                                        "wpbean-quantity-controls-for-woocommerce",
                                    )}
                                </button>
                                <button
                                    onClick={() => onConfirm(true)}
                                    className={`px-6 py-2.5 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-sm ${iconConfig.buttonBg}`}
                                >
                                    {iconConfig.buttonText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ) : null;

    return { confirm, ConfirmDialog };
}
